// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module ConsentBanner
 * @package @geenius/analytics/react-css
 * @description Renders the React CSS consent banner used to capture analytics
 * cookie preferences. The component keeps the vanilla-CSS class contract stable
 * while emitting consent results through callbacks.
 */

import type { ConsentSettings } from "@geenius/analytics";
import { Button, Card, CardContent } from "@geenius/ui/react-css";
import type React from "react";
import { useState } from "react";
import { useAnalyticsCopy } from "../lib/analyticsCopy";
import { cx } from "../lib/cx";
import { FadeIn } from "../lib/FadeIn";
import { useAnalyticsConsent } from "../provider";
import {
  buildConsentSettings,
  ConsentPreferences,
  type ConsentPreferenceValues,
  DEFAULT_CONSENT_PREFERENCES,
} from "./ConsentPreferences";

/**
 * Props for rendering the React CSS analytics consent banner.
 *
 * @property onAccept Called when the user grants analytics consent.
 * @property onReject Called when the user rejects analytics consent.
 * @property show Whether the banner should be visible on first render.
 * @property className Optional class name merged into the banner wrapper.
 */
export interface ConsentBannerProps {
  onAccept: (settings: ConsentSettings) => void;
  onReject: (settings: ConsentSettings) => void;
  show?: boolean;
  className?: string;
}

/**
 * Renders the React CSS consent banner with accept and reject actions.
 *
 * @param props Banner callbacks for accept and reject actions.
 * @returns A consent banner element, or `null` after the user dismisses it.
 */
export function ConsentBanner(
  props: ConsentBannerProps,
): React.ReactElement | null {
  const [visible, setVisible] = useState(props.show !== false);
  const [preferences, setPreferences] = useState<ConsentPreferenceValues>(
    DEFAULT_CONSENT_PREFERENCES,
  );
  const consent = useAnalyticsConsent();
  const copy = useAnalyticsCopy();

  if (!visible) return null;

  const recordConsent = (settings: ConsentSettings): void => {
    consent?.updateConsent(settings);
  };

  const accept = (settings = preferences): void => {
    const consentSettings = buildConsentSettings(settings);
    recordConsent(consentSettings);
    props.onAccept(consentSettings);
    setVisible(false);
  };

  const reject = (): void => {
    const settings = buildConsentSettings(DEFAULT_CONSENT_PREFERENCES);
    recordConsent(settings);
    props.onReject(settings);
    setVisible(false);
  };

  return (
    <FadeIn>
      <section aria-label={copy.t("analytics.consent.regionLabel")}>
        <Card className={cx("gn-analytics-consent-banner", props.className)}>
          <CardContent className="gn-analytics-consent-banner__body">
            <p className="gn-analytics-consent-banner__text">
              {copy.t("analytics.consent.message")}
            </p>
            <ConsentPreferences
              value={preferences}
              onChange={setPreferences}
              showActions={false}
            />
            <div className="gn-analytics-consent-banner__actions">
              <Button
                type="button"
                variant="primary"
                data-analytics="consent-accept"
                onClick={() => {
                  accept({
                    analytics: true,
                    marketing: true,
                    personalization: true,
                  });
                }}
              >
                {copy.t("analytics.consent.acceptAll")}
              </Button>
              <Button
                type="button"
                variant="secondary"
                data-analytics="consent-save"
                onClick={() => accept()}
              >
                {copy.t("analytics.consent.savePreferences")}
              </Button>
              <Button
                type="button"
                variant="secondary"
                data-analytics="consent-reject"
                onClick={reject}
              >
                {copy.t("analytics.consent.reject")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>
    </FadeIn>
  );
}
